/**
 * CloudWatch RUM per-stage wiring — the AppMonitor + identity-pool ids the
 * infra repo's `OmRum{Beta,Prod}` stacks output (same baked-constant pattern
 * as auth-config.ts). Empty ids ⇒ RUM stays off (local dev default; override
 * with the VITE_RUM_* vars to point a dev build at the beta monitor).
 */

import { stage } from './stage.ts';

export interface RumConfig {
  /** AppMonitor id; '' disables RUM entirely. */
  readonly appMonitorId: string;
  readonly identityPoolId: string;
  readonly guestRoleArn: string;
  readonly region: string;
}

// Filled from the OmRum{Beta,Prod} stack outputs (deploy-time constants).
const BETA_APP_MONITOR_ID = 'c9e17b0a-4989-497b-9705-ecb3330b77fd';
const BETA_IDENTITY_POOL_ID = 'us-west-2:3488cf85-6fec-47fe-b1f8-d00da477ce1a';
const BETA_GUEST_ROLE_ARN = 'arn:aws:iam::136332742685:role/oculusmind-rum-guest-beta';
const PROD_APP_MONITOR_ID = 'a309853a-9abe-4e97-8e28-c182fce3febd';
const PROD_IDENTITY_POOL_ID = 'us-west-2:7fad347d-e4a2-4d33-8365-3d4b7c3c47b6';
const PROD_GUEST_ROLE_ARN = 'arn:aws:iam::136332742685:role/oculusmind-rum-guest-prod';

function buildConfig(currentStage: string): RumConfig {
  const env = import.meta.env;
  if (currentStage === 'prod') {
    return {
      appMonitorId: env.VITE_RUM_APP_MONITOR_ID || PROD_APP_MONITOR_ID,
      identityPoolId: env.VITE_RUM_IDENTITY_POOL_ID || PROD_IDENTITY_POOL_ID,
      guestRoleArn: env.VITE_RUM_GUEST_ROLE_ARN || PROD_GUEST_ROLE_ARN,
      region: 'us-west-2',
    };
  }
  if (currentStage === 'beta') {
    return {
      appMonitorId: env.VITE_RUM_APP_MONITOR_ID || BETA_APP_MONITOR_ID,
      identityPoolId: env.VITE_RUM_IDENTITY_POOL_ID || BETA_IDENTITY_POOL_ID,
      guestRoleArn: env.VITE_RUM_GUEST_ROLE_ARN || BETA_GUEST_ROLE_ARN,
      region: 'us-west-2',
    };
  }
  // Local dev: off unless explicitly pointed at a monitor.
  return {
    appMonitorId: env.VITE_RUM_APP_MONITOR_ID || '',
    identityPoolId: env.VITE_RUM_IDENTITY_POOL_ID || '',
    guestRoleArn: env.VITE_RUM_GUEST_ROLE_ARN || '',
    region: 'us-west-2',
  };
}

export const rumConfig: RumConfig = buildConfig(stage);
